/**
 * React Query hooks for fetching analytics data
 * Handles data fetching, caching, and error states
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import api from '../lib/api';
import { AnalyticsFilters } from '../utils/filterValidation';

/**
 * Analytics data types
 */
export interface OverviewMetrics {
  total_responses: number;
  completed_responses: number;
  partial_responses: number;
  avg_duration_ms: number;
  first_response: string;
  last_response: string;
}

export interface Response {
  id: string;
  submitted_at: string;
  respondent_name: string | null;
  respondent_email: string | null;
  status: 'completed' | 'partial' | 'archived';
  meta_json: {
    duration_ms?: number;
    [key: string]: any;
  };
  collector_id: string;
  answers_json: {
    [questionId: string]: any;
  };
  admin_notes: string | null;
  notes_updated_at: string | null;
  archived_at: string | null;
  is_test?: boolean;
}

export interface ResponsesData {
  data: Response[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface TrafficData {
  engagement: {
    [eventType: string]: {
      count: number;
      unique: number;
    };
  };
  utm_sources: Array<{
    source: string;
    count: string;
  }>;
}

export interface TrendPoint {
  date: string;
  responses: string | number;
}

export interface AnalyticsData {
  overview?: OverviewMetrics;
  responses?: ResponsesData;
  traffic?: TrafficData;
  trend?: TrendPoint[];
}

/**
 * Server-cache bypass window. The backend caches analytics-data for 5 minutes;
 * the Refresh button opens a short window during which every refetch carries
 * bypass_cache=true, so "Analytics refreshed" is true at the server layer too
 * (the server re-warms its cache from the bypassed compute).
 */
let bypassServerCacheUntil = 0;

export function beginServerCacheBypass(windowMs: number = 5000): void {
  bypassServerCacheUntil = Date.now() + windowMs;
}

/**
 * Fetch unified analytics data
 */
async function fetchAnalyticsData(
  itemId: string,
  filters: AnalyticsFilters,
  include: string[],
  page: number = 1,
  limit: number = 50,
  showArchived: boolean = false
): Promise<AnalyticsData> {
  const params: any = {
    include: include.join(','),
    page,
    limit,
    showArchived: showArchived.toString()
  };

  if (Object.keys(filters).length > 0) {
    params.filters = JSON.stringify(filters);
  }

  if (Date.now() < bypassServerCacheUntil) {
    params.bypass_cache = 'true';
  }

  const response = await api.get(`/outreach-unified/items/${itemId}/analytics-data`, { params });
  return response.data.data;
}

/**
 * Hook for fetching the daily response trend (last 30 days)
 */
export function useResponseTrend(
  itemId: string,
  filters: AnalyticsFilters,
  enabled: boolean = true
): UseQueryResult<TrendPoint[], Error> {
  return useQuery(
    ['analytics', 'trends', itemId, filters],
    () => fetchAnalyticsData(itemId, filters, ['trend']).then(data => data.trend || []),
    {
      enabled: enabled && !!itemId,
      staleTime: 5 * 60 * 1000
    }
  );
}

/**
 * Hook for fetching overview metrics
 */
export function useOverviewMetrics(
  itemId: string,
  filters: AnalyticsFilters,
  enabled: boolean = true
): UseQueryResult<OverviewMetrics, Error> {
  return useQuery(
    ['analytics', 'overview', itemId, filters],
    () => fetchAnalyticsData(itemId, filters, ['overview']).then(data => {
      const overview = data.overview;
      if (!overview) {
        return {
          total_responses: 0,
          completed_responses: 0,
          partial_responses: 0,
          avg_duration_ms: 0,
          first_response: '',
          last_response: ''
        };
      }
      return {
        total_responses: parseInt(String(overview.total_responses)) || 0,
        completed_responses: parseInt(String(overview.completed_responses)) || 0,
        partial_responses: parseInt(String(overview.partial_responses)) || 0,
        avg_duration_ms: parseFloat(String(overview.avg_duration_ms)) || 0,
        first_response: overview.first_response || '',
        last_response: overview.last_response || ''
      };
    }),
    {
      enabled: enabled && !!itemId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000 // 10 minutes
    }
  );
}

/**
 * Hook for fetching responses (paginated)
 * By default, excludes archived responses (showArchived: false)
 */
export function useResponses(
  itemId: string,
  filters: AnalyticsFilters,
  page: number = 1,
  limit: number = 50,
  enabled: boolean = true,
  showArchived: boolean = false
): UseQueryResult<ResponsesData, Error> {
  return useQuery(
    ['analytics', 'responses', itemId, filters, page, limit, showArchived],
    () => fetchAnalyticsData(itemId, filters, ['responses'], page, limit, showArchived).then(data => {
      // Backend returns responses in { data, total, page, limit, hasMore } format
      const responses = data.responses;
      if (!responses) {
        return {
          data: [],
          total: 0,
          page,
          limit,
          hasMore: false
        };
      }
      return responses;
    }),
    {
      enabled: enabled && !!itemId,
      staleTime: 2 * 60 * 1000, // 2 minutes (shorter for responses)
      cacheTime: 5 * 60 * 1000,
      keepPreviousData: true // Keep old data while fetching new page
    }
  );
}

/**
 * Hook for fetching traffic & engagement data
 */
/** Server-side per-page ceiling — asking for more returns 400. */
const RESPONSES_PAGE_SIZE = 100;

/**
 * Stop after this many rows. Results Quick View holds every row in memory and
 * filters them client-side, so this bounds the browser as much as the network.
 */
export const MAX_ALL_RESPONSES = 1000;

export interface AllResponsesData {
  data: Response[];
  /** How many the outreach actually has, before the cap. */
  total: number;
  /** True when `total` exceeded the cap and rows were left unfetched. */
  truncated: boolean;
}

/**
 * Every response for an outreach, not just the first page.
 *
 * The endpoint caps `limit` at 100 (middleware/validateAnalyticsFilters), and
 * Results Quick View asked for page 1 and stopped — so an outreach with 233
 * responses showed 100 under a heading reading "All Responses (100)". Its
 * search, sorting, district columns and per-question breakdowns all silently
 * described the first page only.
 *
 * This walks the pages, bounded, and reports how many exist so the UI can say
 * "showing 1,000 of 3,101" instead of claiming "all".
 */
export function useAllResponses(
  itemId: string,
  filters: AnalyticsFilters,
  enabled: boolean = true
): UseQueryResult<AllResponsesData, Error> {
  return useQuery(
    ['analytics', 'responses', 'all', itemId, filters],
    async (): Promise<AllResponsesData> => {
      const first = await fetchAnalyticsData(itemId, filters, ['responses'], 1, RESPONSES_PAGE_SIZE);
      const page = first.responses;
      if (!page) return { data: [], total: 0, truncated: false };

      const total = page.total ?? page.data.length;
      const rows: Response[] = [...page.data];
      const ceiling = Math.min(total, MAX_ALL_RESPONSES);
      let hasMore = page.hasMore !== false;
      let pageNumber = 2;

      /*
        Sequential, not parallel: this endpoint sits behind
        outreachAnalyticsLimiter, and firing ten requests at once is how a slow
        screen becomes a rate-limited one.
      */
      while (rows.length < ceiling && hasMore) {
        const next = await fetchAnalyticsData(itemId, filters, ['responses'], pageNumber, RESPONSES_PAGE_SIZE);
        const batch = next.responses?.data ?? [];
        if (batch.length === 0) break;
        rows.push(...batch);
        hasMore = next.responses?.hasMore !== false;
        pageNumber += 1;
      }

      return {
        data: rows.slice(0, MAX_ALL_RESPONSES),
        total,
        truncated: total > MAX_ALL_RESPONSES,
      };
    },
    {
      enabled: enabled && !!itemId,
      staleTime: 2 * 60 * 1000,
      cacheTime: 5 * 60 * 1000,
      keepPreviousData: true,
    }
  );
}

export function useTrafficData(
  itemId: string,
  filters: AnalyticsFilters,
  enabled: boolean = true
): UseQueryResult<TrafficData, Error> {
  return useQuery(
    ['analytics', 'traffic', itemId, filters],
    () => fetchAnalyticsData(itemId, filters, ['traffic']).then(data => data.traffic!),
    {
      enabled: enabled && !!itemId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000
    }
  );
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * Download a report view as PDF or Excel (authenticated)
 */
export async function downloadViewExport(
  itemId: string,
  viewId: string,
  format: 'pdf' | 'excel',
  viewName: string = 'report'
): Promise<void> {
  const response = await api.get(
    `/outreach/${itemId}/analytics/views/${viewId}/export`,
    { params: { format }, responseType: 'blob' }
  );
  const safeName = viewName.replace(/[^\w-]+/g, '-').toLowerCase();
  triggerBlobDownload(
    new Blob([response.data]),
    `report-${safeName}-${Date.now()}.${format === 'pdf' ? 'pdf' : 'xlsx'}`
  );
}

/**
 * Trigger export download
 */
export async function exportResponses(
  itemId: string,
  format: 'csv' | 'excel',
  filters: AnalyticsFilters
): Promise<void> {
  const params: any = {
    format
  };
  
  if (Object.keys(filters).length > 0) {
    params.filters = JSON.stringify(filters);
  }
  
  const response = await api.get(
    `/outreach-unified/items/${itemId}/responses/export`,
    {
      params,
      responseType: 'blob'
    }
  );
  
  // Trigger browser download
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `outreach-responses-${itemId}-${Date.now()}.${format === 'excel' ? 'xlsx' : 'csv'}`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
